/**
 * Configuration for cache persistence in localStorage
 * Allows certain queries to persist between browser sessions
 */

import * as Sentry from '@sentry/react';

// Query keys that will be saved in localStorage
export const PERSISTED_QUERY_KEYS = {
  // Reference data that changes little
  REFERENCE_DATA: [
    'countriesAll',
    'segmentsAll',
    'industriesAll',
    'lifeCyclesAll',
    'documentTypesAll',
  ],

  // User data
  USER_DATA: ['user', 'profile', 'permissions'],

  // System configurations
  SYSTEM_CONFIG: ['systemSettings', 'themeConfig', 'languageConfig'],
} as const;

// Function to check if a query should be persisted
export function shouldPersistQuery(queryKey: string[]): boolean {
  const key = queryKey[0] as string;

  return (
    PERSISTED_QUERY_KEYS.REFERENCE_DATA.includes(key as any) ||
    PERSISTED_QUERY_KEYS.USER_DATA.includes(key as any) ||
    PERSISTED_QUERY_KEYS.SYSTEM_CONFIG.includes(key as any)
  );
}

// Function to get the localStorage key for a query
export function getStorageKey(queryKey: string[]): string {
  return `react-query-${queryKey.join('-')}`;
}

// Function to save data to localStorage
export function saveToStorage(queryKey: string[], data: any): void {
  try {
    const storageKey = getStorageKey(queryKey);
    const item = {
      data,
      timestamp: Date.now(),
      queryKey,
    };
    localStorage.setItem(storageKey, JSON.stringify(item));
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'queryPersistence',
        operation: 'saveToStorage',
      },
      extra: {
        queryKey,
      },
    });
  }
}

// Function to load data from localStorage
export function loadFromStorage(queryKey: string[]): any | null {
  try {
    const storageKey = getStorageKey(queryKey);
    const item = localStorage.getItem(storageKey);

    if (!item) return null;

    const parsed = JSON.parse(item);
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours

    // Check if data hasn't expired
    if (Date.now() - parsed.timestamp > maxAge) {
      localStorage.removeItem(storageKey);
      return null;
    }

    return parsed.data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'queryPersistence',
        operation: 'loadFromStorage',
      },
      extra: {
        queryKey,
      },
    });
    return null;
  }
}

// Function to clean expired data from localStorage
export function cleanupExpiredStorage(): void {
  try {
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    const now = Date.now();

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('react-query-')) {
        try {
          const item = JSON.parse(localStorage.getItem(key) || '');
          if (now - item.timestamp > maxAge) {
            localStorage.removeItem(key);
          }
        } catch {
          // If it can't be parsed, remove the entry
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'queryPersistence',
        operation: 'cleanupExpiredStorage',
      },
    });
  }
}

// Function to clear all React Query cache from localStorage
export function clearQueryCache(): void {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('react-query-')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'queryPersistence',
        operation: 'clearQueryCache',
      },
    });
  }
}

// Helper function to create persisted query config for useQueries
export const createPersistedQueryConfig = (
  queryKey: readonly string[],
  queryFn: () => Promise<any>,
  enabled: boolean
) => ({
  queryKey: [...queryKey],
  queryFn,
  enabled,
  staleTime: 30 * 60 * 1000, // 30 minutes
  cacheTime: 60 * 60 * 1000, // 1 hour
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
  // Cache persistence
  initialData: () => {
    try {
      const storageKey = `react-query-${queryKey.join('-')}`;
      const item = localStorage.getItem(storageKey);
      if (item) {
        const parsed = JSON.parse(item);
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        if (Date.now() - parsed.timestamp <= maxAge) {
          return parsed.data;
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'localStorage_load', queryKey: queryKey[0] },
      });
    }
    return undefined;
  },
  onSuccess: (data: any) => {
    try {
      const storageKey = `react-query-${queryKey.join('-')}`;
      const item = {
        data,
        timestamp: Date.now(),
        queryKey,
      };
      localStorage.setItem(storageKey, JSON.stringify(item));
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'localStorage_save', queryKey: queryKey[0] },
      });
    }
  },
});
